import { Dispatch } from 'redux';
import SponsoredMessageController, {
  RetrieveAllResponse,
} from '../controllers/SponsoredMessageController';
import sponsoredMessagesHandler from '../errorHandlers/sponsored-messages-handler';
import {
  ExtendedSponsoredMessageModel,
  SponsoredMessageModel,
} from '../model/SponsoredMessageModel';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';
import commonHandler, {
  permissionsHandler,
} from '../errorHandlers/common-handler';
import ConnectedPageActions from './ConnectedPageCRUDActions';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';

class SponsoredMessageCRUDActions extends CrudActionContainer {
  resetAction: string;

  constructor() {
    super('SPONSORED_MESSAGES');
    this.resetAction = super.makeActionKey('RESET');
  }

  create(botId: number, params: Partial<ExtendedSponsoredMessageModel>) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<SponsoredMessageModel> => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SponsoredMessageController.postSponsoredMessage(
          botId,
          params,
        );
        dispatch(this.retrieved(response.sponsoredMessage));
        return response.sponsoredMessage;
      } catch (e) {
        return sponsoredMessagesHandler(dispatch)(e);
      }
    };
  }

  update(
    botId: number,
    sponsoredMessageId: number,
    params: Partial<ExtendedSponsoredMessageModel>,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<SponsoredMessageModel> => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await SponsoredMessageController.putSponsoredMessage(
          botId,
          sponsoredMessageId,
          params,
        );
        dispatch(this.updateLocal(response.sponsoredMessage));
        return response.sponsoredMessage;
      } catch (e) {
        return sponsoredMessagesHandler(dispatch)(e);
      }
    };
  }

  publish(
    botId: number,
    sponsoredMessageId: number,
    changes: Partial<SponsoredMessageModel>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      try {
        await SponsoredMessageController.publish(
          botId,
          sponsoredMessageId,
          changes,
        );
        dispatch({
          type: ConnectedPageActions.adPublished,
          key: 'hasSponsoredAds',
        });
      } catch (e) {
        dispatch(this.unbusy());
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  retrieveAll(
    botId: number,
    page?: number,
    perPage?: number,
    orderBy?: string,
    orderDirection?: 'asc' | 'desc',
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<RetrieveAllResponse> => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await SponsoredMessageController.retrieveAll(botId, {
          page,
          perPage,
          orderBy,
          orderDirection,
        });
        const { sponsoredMessages } = response;
        dispatch(this.retrievedAll(sponsoredMessages));
        return response;
      } catch (e) {
        dispatch(this.unbusy());
        return commonHandler(dispatch)(e);
      }
    };
  }

  prepareReplacementPage(botId: number, entityId: number, pageId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response =
          await SponsoredMessageController.prepareReplacementPage(
            botId,
            entityId,
            pageId,
          );
        dispatch(this.updateLocal(response.sponsoredMessage));
        return response.sponsoredMessage;
      } catch (e) {
        return sponsoredMessagesHandler(dispatch)(e);
      }
    };
  }

  retrieveById(
    botId: number,
    sponsoredMessageId: number,
    unbusy: boolean = true,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<SponsoredMessageModel> => {
      try {
        dispatch(this.busy(this.retrieveAction));
        const sponsoredMessage = await SponsoredMessageController.retrieveById(
          botId,
          sponsoredMessageId,
        );
        if (unbusy) {
          dispatch(this.updateLocal(sponsoredMessage));
        }
        return sponsoredMessage;
      } catch (e) {
        dispatch(this.unbusy());
        return commonHandler(dispatch)(e);
      }
    };
  }

  delete(botId: number, sponsoredMessage: SponsoredMessageModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteAction));
        const response =
          await SponsoredMessageController.deleteSponsoredMessage(
            botId,
            sponsoredMessage.id,
          );
        dispatch(this.deleteLocal(sponsoredMessage));
        return response;
      } catch (e) {
        dispatch(this.unbusy());
        return permissionsHandler(dispatch, this)(e);
      }
    };
  }

  reset() {
    return {
      type: this.resetAction,
    };
  }
}

const sponsoredMessageCRUDActions = new SponsoredMessageCRUDActions();

export default sponsoredMessageCRUDActions;

type RetrieveAllOptions = {
  perPage?: number;
  page?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
};

export const useSponsoredMessageCRUDActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () => ({
      retrieveAll: (chatbotId: number, options?: RetrieveAllOptions) =>
        dispatch(
          sponsoredMessageCRUDActions.retrieveAll(
            chatbotId,
            options?.page,
            options?.perPage,
            options?.orderBy,
            options?.orderDirection,
          ),
        ),
      create: (
        ...args: Parameters<typeof sponsoredMessageCRUDActions.create>
      ) => dispatch(sponsoredMessageCRUDActions.create(...args)),
      delete: (
        ...args: Parameters<typeof sponsoredMessageCRUDActions.delete>
      ) => dispatch(sponsoredMessageCRUDActions.delete(...args)),
    }),
    [dispatch],
  );
};
